import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { OutreachHome } from '../outreachTypes';
import { percentOf } from '../outreachFormat';
import { homeMessages as messages } from './homeMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HomeTotalsProps {
  totals: OutreachHome['totals28d'];
}

/**
 * The 28-day totals (design outreach a1): sent, listened (the pitch page play; no open pixel),
 * replies and placements. "Found by Music24" is a placement that the playlist data showed.
 */
const HomeTotalsComponent: React.FC<HomeTotalsProps> = ({ totals }) => {
  const intl = useIntl();
  const cards = useMemo(
    () => [
      { key: 'sent', label: messages.totalSent, value: totals.sent, hint: null },
      {
        key: 'listened',
        label: messages.totalListened,
        value: totals.listened,
        hint: intl.formatMessage(messages.totalListenedHint, { percent: percentOf(totals.listened, totals.sent) }),
      },
      {
        key: 'replied',
        label: messages.totalReplied,
        value: totals.replied,
        hint: intl.formatMessage(messages.totalRepliedHint, { percent: percentOf(totals.replied, totals.sent) }),
      },
      {
        key: 'placed',
        label: messages.totalPlaced,
        value: totals.placed,
        hint: intl.formatMessage(messages.totalPlacedHint, {
          detected: totals.placedDetected,
          fromReplies: Math.max(0, totals.placed - totals.placedDetected),
        }),
      },
    ],
    [intl, totals]
  );
  return (
    <div className={styles.totals}>
      {cards.map((card) => (
        <div
          key={card.key}
          className={clsx(styles.statCard, card.key === 'placed' && styles.statPos)}
          data-testid={`outreach-total-${card.key}`}
        >
          <div className={styles.statKey}>{intl.formatMessage(card.label)}</div>
          <div className={styles.statValue}>{intl.formatNumber(card.value)}</div>
          {card.hint !== null && <div className={styles.statHint}>{withBrandWord(card.hint)}</div>}
        </div>
      ))}
    </div>
  );
};

export const HomeTotals = TypedMemo(HomeTotalsComponent);
